import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton, StatCard, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { RefreshCw, Warehouse, Package, Download } from "lucide-react";

const StockTable = ({ rows, filter, cols, testId }) => (
  <table className="w-full text-sm" data-testid={testId}>
    <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
      <tr>{cols.map((c) => <th key={c.label} className="text-left px-4 py-3 font-semibold">{c.label}</th>)}</tr>
    </thead>
    <tbody>
      {rows.filter((r) => ((r.name || r.title || "") + r.sku + (r.asin || "")).toLowerCase().includes(filter.toLowerCase())).slice(0, 200).map((r, i) => (
        <tr key={`${r.sku}-${r.location || i}`} className="border-t border-border hover:bg-muted/40 transition-colors">
          {cols.map((c) => <td key={c.label} className={`px-4 py-2.5 ${c.cls || ""}`}>{c.get(r)}</td>)}
        </tr>
      ))}
    </tbody>
  </table>
);

export const FbaStocks = () => {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [fcSyncing, setFcSyncing] = useState(false);
  const [filter, setFilter] = useState("");
  const [view, setView] = useState("all");
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/tools/fba/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fba-stocks-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded — full FBA dataset (3 sheets)");
    } catch {
      toast.error("Excel export failed");
    } finally {
      setExporting(false);
    }
  };

  const load = () => api.get("/tools/fba").then(({ data }) => {
    setData(data);
    setFcSyncing(!!data.fc_report?.running);
  });
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!fcSyncing) return;
    const t = setInterval(async () => {
      const { data: d } = await api.get("/tools/fba");
      if (!d.fc_report?.running) {
        clearInterval(t);
        setFcSyncing(false);
        if (d.fc_report?.error) toast.error(`Warehouse report failed: ${d.fc_report.error}`);
        else toast.success(`Warehouse report loaded — ${d.fc_report?.rows || 0} rows`);
        setData(d);
      }
    }, 8000);
    return () => clearInterval(t);
  }, [fcSyncing]);

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/tools/fba/sync");
      toast.info("FBA stock refresh started — totals update within ~2 minutes");
      setTimeout(() => { load(); setSyncing(false); }, 120000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  const fcSync = async () => {
    await api.post("/tools/fba/warehouse-sync");
    toast.info("Requested warehouse-wise report from Amazon (1–5 min)…");
    setFcSyncing(true);
  };

  if (!data) return <Skeleton className="h-64" />;

  const own = data.own_warehouse || { rows: [], units: 0 };
  const wareiq = data.wareiq || { rows: [], units: 0, locations: [] };
  const afc = data.amazon_fc || { rows: [], units: 0, locations: [] };

  return (
    <div className="space-y-6" data-testid="fba-stocks">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          Stock split between <strong>your warehouse (KNQ0)</strong>, <strong>WareIQ sites (XXXD, XHEI, XHEH, XLJD)</strong> and <strong>Amazon's FBA fulfilment centres</strong>.
          {data.last_updated && ` Totals updated ${new Date(data.last_updated).toLocaleString("en-IN")}.`}
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-full" onClick={exportExcel} disabled={exporting} data-testid="fba-export-button">
            <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            {exporting ? "Preparing…" : "Export to Excel"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={fcSync} disabled={fcSyncing} data-testid="fc-sync-button">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${fcSyncing ? "animate-spin" : ""}`} aria-hidden="true" />
            {fcSyncing ? "Amazon preparing report…" : "Refresh warehouse split"}
          </Button>
          <Button size="sm" className="rounded-full" onClick={sync} disabled={syncing} data-testid="fba-sync-button">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} aria-hidden="true" />
            {syncing ? "Refreshing…" : "Refresh totals"}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Our Warehouse (KNQ0)" value={own.units.toLocaleString("en-IN")} sub={`${own.rows.length} SKUs`} testId="own-wh-kpi" />
        <StatCard label="WareIQ Warehouses" value={wareiq.units.toLocaleString("en-IN")} sub={wareiq.locations.join(", ") || "XXXD, XHEI, XHEH, XLJD"} testId="wareiq-kpi" />
        <StatCard label="Amazon FBA Centres" value={afc.units.toLocaleString("en-IN")} sub={`${afc.locations.length} locations`} testId="amazon-fc-kpi" />
        <StatCard label="Total (FBA network)" value={data.total_units.toLocaleString("en-IN")} sub={`${data.total_skus} SKUs`} testId="fba-units-kpi" />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by product, SKU or ASIN…" data-testid="fba-search-input"
          className="w-full max-w-sm h-9 rounded-full border border-border bg-card px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        <div className="flex rounded-full border border-border bg-card p-1" data-testid="fba-view-toggle">
          {[["all", "All views"], ["amazon", "Amazon FBA"], ["wareiq", "WareIQ"], ["own", "Our warehouse"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`fba-view-${k}`}
              className={`px-4 h-7 rounded-full text-xs font-semibold transition-colors ${view === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {(view === "all" || view === "own") && (
      <section className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-blue-600" aria-hidden="true" />
          <h3 className="font-heading text-base font-semibold tracking-tight">Our Warehouse — KNQ0 ({own.rows.length} SKUs)</h3>
        </div>
        {own.rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No warehouse-wise data yet — click "Refresh warehouse split" (Amazon generates the ledger report in 1–5 minutes).</p>
        ) : (
          <StockTable rows={own.rows} filter={filter} testId="own-wh-table" cols={[
            { label: "Product", get: (r) => <div className="flex items-center gap-2.5"><SkuThumb sku={r.sku} alt={r.title || r.sku} /><span className="font-medium">{r.title || r.sku}</span></div> },
            { label: "SKU", get: (r) => r.sku, cls: "font-mono text-xs text-muted-foreground" },
            { label: "ASIN", get: (r) => r.asin, cls: "font-mono text-xs text-muted-foreground" },
            { label: "Units", get: (r) => <strong>{r.qty}</strong> },
          ]} />
        )}
      </section>
      )}

      {(view === "all" || view === "wareiq") && (
      <section className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Warehouse className="h-4 w-4 text-violet-600" aria-hidden="true" />
          <h3 className="font-heading text-base font-semibold tracking-tight">WareIQ Warehouses ({wareiq.locations.join(", ") || "XXXD, XHEI, XHEH, XLJD"}) — {wareiq.rows.length} rows</h3>
        </div>
        {wareiq.rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No WareIQ stock rows yet — click "Refresh warehouse split" above.</p>
        ) : (
          <StockTable rows={wareiq.rows} filter={filter} testId="wareiq-table" cols={[
            { label: "Product", get: (r) => <div className="flex items-center gap-2.5"><SkuThumb sku={r.sku} alt={r.title || r.sku} /><span className="font-medium">{r.title || r.sku}</span></div> },
            { label: "SKU", get: (r) => r.sku, cls: "font-mono text-xs text-muted-foreground" },
            { label: "Site", get: (r) => r.location, cls: "font-mono text-xs" },
            { label: "Units", get: (r) => <strong>{r.qty}</strong> },
          ]} />
        )}
      </section>
      )}

      {(view === "all" || view === "amazon") && (
      <section className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Package className="h-4 w-4 text-amber-600" aria-hidden="true" />
          <h3 className="font-heading text-base font-semibold tracking-tight">Amazon FBA Centres ({afc.locations.join(", ") || "—"})</h3>
        </div>
        {afc.rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No warehouse-wise data yet — click "Refresh warehouse split" above.</p>
        ) : (
          <StockTable rows={afc.rows} filter={filter} testId="amazon-fc-table" cols={[
            { label: "Product", get: (r) => <div className="flex items-center gap-2.5"><SkuThumb sku={r.sku} alt={r.title || r.sku} /><span className="font-medium">{r.title || r.sku}</span></div> },
            { label: "SKU", get: (r) => r.sku, cls: "font-mono text-xs text-muted-foreground" },
            { label: "FC", get: (r) => r.location, cls: "font-mono text-xs" },
            { label: "Units", get: (r) => <strong>{r.qty}</strong> },
          ]} />
        )}
      </section>
      )}

      {(view === "all" || view === "amazon") && (
      <section className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-heading text-base font-semibold tracking-tight">FBA network totals (all locations combined)</h3>
        </div>
        <StockTable rows={data.stocks} filter={filter} testId="fba-table" cols={[
          { label: "Product", get: (r) => <div className="flex items-center gap-2.5"><SkuThumb sku={r.sku} alt={r.name || r.sku} /><span className="font-medium">{r.name || r.sku}</span></div> },
          { label: "SKU", get: (r) => r.sku, cls: "font-mono text-xs text-muted-foreground" },
          { label: "ASIN", get: (r) => r.asin, cls: "font-mono text-xs text-muted-foreground" },
          { label: "Fulfillable", get: (r) => r.fulfillable },
          { label: "Inbound", get: (r) => r.inbound },
          { label: "Total", get: (r) => <strong>{r.total}</strong> },
        ]} />
      </section>
      )}
    </div>
  );
};
